import type { LanguageModelUsage } from "ai";
import type { AgentUIMessage } from "./types";

type UsageRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UsageRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isTokenCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

const tokenCount = (value: unknown): number | undefined =>
  isTokenCount(value) ? value : undefined;

const isInvalidTokenCount = (value: unknown): boolean =>
  value !== undefined && !isTokenCount(value);

/**
 * Normalizes unversioned persisted usage into the complete AI SDK v7 shape.
 *
 * @remarks
 * Existing chats contain the flat AI SDK v5/v6 `cachedInputTokens` and
 * `reasoningTokens` fields, while new turns persist the nested v7 detail fields.
 * Metadata enters this boundary as `unknown`, so every token count is validated before
 * field precedence is applied; malformed latest metadata can then be skipped safely.
 */
export function normalizeLanguageModelUsage(
  value: unknown,
): LanguageModelUsage | undefined {
  if (!isRecord(value)) return undefined;
  if (
    (value.inputTokenDetails !== undefined &&
      !isRecord(value.inputTokenDetails)) ||
    (value.outputTokenDetails !== undefined &&
      !isRecord(value.outputTokenDetails))
  ) {
    return undefined;
  }

  const inputDetails = isRecord(value.inputTokenDetails)
    ? value.inputTokenDetails
    : undefined;
  const outputDetails = isRecord(value.outputTokenDetails)
    ? value.outputTokenDetails
    : undefined;

  const persistedTokenCounts = [
    value.inputTokens,
    inputDetails?.noCacheTokens,
    inputDetails?.cacheReadTokens,
    value.cachedInputTokens,
    inputDetails?.cacheWriteTokens,
    value.outputTokens,
    outputDetails?.textTokens,
    outputDetails?.reasoningTokens,
    value.reasoningTokens,
    value.totalTokens,
  ];
  if (persistedTokenCounts.some(isInvalidTokenCount)) return undefined;

  const inputTokens = tokenCount(value.inputTokens);
  const noCacheTokens = tokenCount(inputDetails?.noCacheTokens);
  const cacheReadTokens =
    tokenCount(inputDetails?.cacheReadTokens) ??
    tokenCount(value.cachedInputTokens);
  const cacheWriteTokens = tokenCount(inputDetails?.cacheWriteTokens);
  const outputTokens = tokenCount(value.outputTokens);
  const textTokens = tokenCount(outputDetails?.textTokens);
  const reasoningTokens =
    tokenCount(outputDetails?.reasoningTokens) ??
    tokenCount(value.reasoningTokens);
  const totalTokens = tokenCount(value.totalTokens);

  if (
    [
      inputTokens,
      noCacheTokens,
      cacheReadTokens,
      cacheWriteTokens,
      outputTokens,
      textTokens,
      reasoningTokens,
      totalTokens,
    ].every((tokenCount) => tokenCount === undefined)
  ) {
    return undefined;
  }

  return {
    inputTokens,
    inputTokenDetails: {
      noCacheTokens,
      cacheReadTokens,
      cacheWriteTokens,
    },
    outputTokens,
    outputTokenDetails: {
      textTokens,
      reasoningTokens,
    },
    totalTokens,
    ...(isRecord(value.raw)
      ? { raw: value.raw as LanguageModelUsage["raw"] }
      : {}),
  };
}

/** A completed request and its final call, kept together with the measured model. */
export type ChatTokenUsage = {
  tokenUsage: LanguageModelUsage;
  finalStepUsage?: LanguageModelUsage;
  modelId?: string;
  isPrevious?: boolean;
  isPartial?: boolean;
};

/** Restores the latest measured request without pairing it with another message. */
export function getLatestTokenUsage(
  messages: AgentUIMessage[],
): ChatTokenUsage | undefined {
  let isPrevious = false;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!message || message.role === "system") continue;
    if (message.role !== "assistant") {
      isPrevious = true;
      continue;
    }
    const tokenUsage = normalizeLanguageModelUsage(
      message.metadata?.tokenUsage,
    );
    if (!tokenUsage) {
      isPrevious = true;
      continue;
    }
    return {
      tokenUsage,
      isPrevious,
      isPartial: message.metadata?.tokenUsageIsPartial === true,
      finalStepUsage: normalizeLanguageModelUsage(
        message.metadata?.finalStepUsage,
      ),
      modelId:
        typeof message.metadata?.modelId === "string"
          ? message.metadata.modelId
          : undefined,
    };
  }
  return undefined;
}

/** Unknown totals stay unknown; cache and reasoning are already included. */
export function getTotalTokens(usage?: LanguageModelUsage): number | undefined {
  if (!usage) return undefined;
  if (usage.totalTokens !== undefined) return usage.totalTokens;
  if (usage.inputTokens === undefined || usage.outputTokens === undefined)
    return undefined;
  return usage.inputTokens + usage.outputTokens;
}
